import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';

export const portalId = 'autocomplete-portal'; // Export the portal ID

interface PortalProps {
  children: React.ReactNode;
  isOpen: boolean;
  anchorElement?: HTMLElement | React.RefObject<HTMLElement>;
  className?: string;
}

export const Portal: React.FC<PortalProps> = ({
  children,
  isOpen,
  anchorElement,
  className
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const portalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!anchorElement || !isOpen) return;

    const updatePosition = () => {
      const element = 'current' in anchorElement ? anchorElement.current : anchorElement;
      if (element) {
        const rect = element.getBoundingClientRect();
        const dropdownHeight = 300; // max-height of dropdown
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Position above if not enough space below
        const shouldPositionAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

        setPosition({
          top: shouldPositionAbove
            ? rect.top + window.scrollY - dropdownHeight - 8
            : rect.bottom + window.scrollY + 8,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      }
    };

    updatePosition();

    const handleResize = () => updatePosition();
    const handleScroll = () => updatePosition();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    // Use ResizeObserver if available for anchor element size changes
    let resizeObserver: ResizeObserver | null = null;
    const element = 'current' in anchorElement ? anchorElement.current : anchorElement;

    if (element && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(updatePosition);
      resizeObserver.observe(element);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
      resizeObserver?.disconnect();
    };
  }, [anchorElement, isOpen]);

  if (!isOpen) return null;

  // Ensure portal container exists
  let portalContainer = document.getElementById(portalId);
  if (!portalContainer) {
    portalContainer = document.createElement('div');
    portalContainer.id = portalId;
    portalContainer.style.zIndex = '9999';
    document.body.appendChild(portalContainer);
  }

  const content = (
    <div
      ref={portalRef}
      className={className}
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        width: position.width,
        zIndex: 9999,
      }}
    >
      {children}
    </div>
  );

  return createPortal(content, portalContainer);
};
